import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CompanyDetailsDtoModel, DelServiceProviderDtoModel, VendorDtoModel } from '@well/domain';
import { SignupWizardFeatureService } from '../../service/signup-wizard-feature.service';
import { signupSubscriptionStepSegment } from '@well/navigation';
import { forkJoin, Observable, of, switchMap, take } from 'rxjs';
import { ErrorService } from '@irian-ro/shared-components-module';
import { ToastMessageOptions } from 'primeng/api';
import { LoggedInUser, LoginDetails, LoginService, UserContext } from '@irian-ro/auth-shared-module';
import { CompanyType, LegalEntityModel } from '@well/common-libs/legal-entity-management/domain-model';
import { VendorResourceService } from '@well/resource-services';

@Component({
  selector: 'nextwell-signup-company-info',
  templateUrl: './company-info.component.html',
  styleUrls: ['./company-info.component.scss'],
})
export class CompanyInfoComponent implements OnInit {

  companyType: CompanyType = CompanyType.customer;
  iconClass = 'ui-icon-domain';

  errorMessages: Observable<ToastMessageOptions[]> | undefined = undefined;

  confirmDialogVisible = false;

  loginDetails: LoginDetails = new LoginDetails();

  legalEntityUuid: string | undefined = undefined;

  confirmPasswordErrorMessages: ToastMessageOptions[] | undefined = undefined;

  vendor: VendorDtoModel = new VendorDtoModel();

  constructor(private route: ActivatedRoute,
              private signupWizardFeatureService: SignupWizardFeatureService,
              private router: Router,
              private errorService: ErrorService,
              private loginService: LoginService,
              private userContext: UserContext,
              private vendorResourceService: VendorResourceService) {}

  ngOnInit(): void {

    this.route.params.subscribe((params: Params) => {
      this.companyType = params['type'];
      this.updateIconClass();
    })

    this.userContext
      .getCurrentUser()
      .pipe((take(1)))
      .subscribe((user: LoggedInUser) => {
        this.loginDetails.username = user.name;
      })
  }

  private updateIconClass(){
    switch (this.companyType){
      case CompanyType.customer: {
        this.iconClass = 'ui-icon-domain';
        break;
      }
      case CompanyType.forwarder: {
        this.iconClass = 'ui-icon-rv-hookup';
        break;
      }
      case CompanyType.service_provider: {
        this.iconClass = 'ui-icon-local-shipping';
        break;
      }
      case CompanyType.customer_with_fleet: {
        this.iconClass = 'ui-icon-domain';
        break;
      }
      default: {
        this.iconClass = '';
        break;
      }
    }
  }

  onSave(companyDetails: CompanyDetailsDtoModel){
    this.addCreateServiceProviderField(companyDetails);
    if(companyDetails.legalEntityDTO?.uuid){
      this.signupWizardFeatureService.updateCompanyDetails(companyDetails).pipe(
        switchMap((legalEntity) => {
          if(this.companyType == CompanyType.customer_with_fleet){
            this.createVendorForCustomerWithFleet(legalEntity);
          }
          return forkJoin([this.signupWizardFeatureService.assignCurrentUserToCompanySuperUser(legalEntity.uuid), of(legalEntity.uuid)])
        }),
        switchMap(([user, legalEntityUuid]) => {
          return this.signupWizardFeatureService.assignCompanyTypeToValidLegalEntity(legalEntityUuid, this.companyType);
        })
      ).subscribe(
        {
           next: (legalEntity)  => {
             this.legalEntityUuid = legalEntity.uuid || undefined;
             if(this.companyType == CompanyType.customer_with_fleet){
               this.vendor.owner = legalEntity.uuid
               this.saveVendor();
             }
              this.showConfirmPasswordDialog();
            },
          error: err => {
            this.errorMessages = this.errorService.prepareErrorsForDisplay(err, 'signup.error');
          }
      })
    }
    else{
      this.signupWizardFeatureService.saveCompanyDetails(companyDetails).pipe(
        switchMap((legalEntity) => {
          if(this.companyType == CompanyType.customer_with_fleet){
            this.createVendorForCustomerWithFleet(legalEntity);
          }
          return forkJoin([this.signupWizardFeatureService.assignCurrentUserToCompanySuperUser(legalEntity.uuid), of(legalEntity.uuid)])
        }),
        switchMap(([user, legalEntityUuid]) => {
          return this.signupWizardFeatureService.assignCompanyTypeToValidLegalEntity(legalEntityUuid, this.companyType);
        })
      ).subscribe({
        next: (legalEntity)  => {
          this.legalEntityUuid = legalEntity.uuid || undefined;
          if(this.companyType == CompanyType.customer_with_fleet){
            this.vendor.owner = legalEntity.uuid
            this.saveVendor();
          }

          this.showConfirmPasswordDialog();
        },
        error: err => {
          this.errorMessages = this.errorService.prepareErrorsForDisplay(err, 'signup.error');
        }
      })
    }
  }

  private createVendorForCustomerWithFleet(legalEntity: LegalEntityModel){
    this.vendor.serviceProvider = new DelServiceProviderDtoModel()
    this.vendor.tags = [];
    this.vendor.owner = this.vendor.serviceProvider.owner || null;
    this.vendor.serviceProvider.legalEntity = legalEntity;
  }

  private saveVendor(){
    return this.vendorResourceService.save(this.vendor).subscribe();
  }

  private addCreateServiceProviderField(companyDetailsDto: CompanyDetailsDtoModel){
    companyDetailsDto.createServiceProvider = (this.companyType === CompanyType.forwarder ||
                                                this.companyType === CompanyType.service_provider ||
                                                this.companyType === CompanyType.customer_with_fleet);
  }

  showConfirmPasswordDialog(){
    this.confirmDialogVisible = true;
  }

  confirmPassword(){
    this.loginService.doLogin(this.loginDetails).subscribe({
      next: () => {
        this.confirmDialogVisible = false;
        this.router.navigate([`../../${signupSubscriptionStepSegment}`, this.legalEntityUuid], {relativeTo: this.route});
      }
    })
  }
}
